import { useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { User, Loader2, Camera } from 'lucide-react';

export default function ProfilePage() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    bio: profile?.bio || '',
    paypal_merchant_id: profile?.paypal_merchant_id || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.full_name,
          bio: formData.bio,
          paypal_merchant_id: formData.paypal_merchant_id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile?.id);

      if (error) throw error;

      toast({
        title: 'Profilo aggiornato',
        description: 'Le tue informazioni sono state salvate con successo!',
      });
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Profilo
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestisci le tue informazioni personali
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Foto profilo</CardTitle>
            <CardDescription>
              Carica una foto per il tuo profilo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-3xl font-semibold">
                {profile?.full_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <Button variant="outline">
                <Camera className="w-4 h-4 mr-2" />
                Carica foto
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Informazioni personali</CardTitle>
            <CardDescription>
              Aggiorna le tue informazioni di base
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile?.email}
                  disabled
                />
                <p className="text-xs text-gray-500">
                  L'email non può essere modificata
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Nome completo</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  placeholder="Il tuo nome"
                  value={formData.full_name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Raccontaci qualcosa di te..."
                  rows={4}
                  value={formData.bio}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              {profile?.role === 'docente' && (
                <div className="space-y-2">
                  <Label htmlFor="paypal_merchant_id">PayPal Merchant ID</Label>
                  <Input
                    id="paypal_merchant_id"
                    name="paypal_merchant_id"
                    placeholder="Il tuo PayPal Merchant ID"
                    value={formData.paypal_merchant_id}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500">
                    Necessario per ricevere i pagamenti dei corsi
                  </p>
                </div>
              )}

              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  'Salva modifiche'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Informazioni account</CardTitle>
            <CardDescription>
              Dettagli del tuo account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Ruolo</span>
                <span className="font-medium capitalize">{profile?.role}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Data registrazione</span>
                <span className="font-medium">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT') : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Ultimo aggiornamento</span>
                <span className="font-medium">
                  {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString('it-IT') : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
